/**
 * Browser Detection Utility
 * 
 * Provides functions to detect browser type and capabilities for handling different
 * video playback scenarios, especially for Safari and HLS support.
 */

/**
 * Get detailed browser information
 * @returns {object} Browser information object
 */
export const getBrowserInfo = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  const browser = {
    name: 'unknown',
    version: 'unknown',
    isSafari: false,
    isChrome: false,
    isFirefox: false,
    isEdge: false,
    isMobile: false,
    hasNativeHlsSupport: false,
    supportsCaptureStream: false,
    supportsMediaRecorder: false
  };

  // Detect Safari
  if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) {
    browser.name = 'Safari';
    browser.isSafari = true;
    browser.hasNativeHlsSupport = true;
    
    // Safari version detection
    const safariVersion = userAgent.match(/Version\/([0-9.]+)/);
    if (safariVersion) {
      browser.version = safariVersion[1];
    }
  }
  
  // Detect Chrome
  else if (/Chrome/.test(userAgent) && !/Edg/.test(userAgent)) {
    browser.name = 'Chrome';
    browser.isChrome = true;
    
    const chromeVersion = userAgent.match(/Chrome\/([0-9.]+)/);
    if (chromeVersion) {
      browser.version = chromeVersion[1];
    }
  }
  
  // Detect Edge
  else if (/Edg/.test(userAgent)) {
    browser.name = 'Edge';
    browser.isEdge = true;
    
    const edgeVersion = userAgent.match(/Edg\/([0-9.]+)/);
    if (edgeVersion) {
      browser.version = edgeVersion[1];
    }
  }
  
  // Detect Firefox
  else if (/Firefox/.test(userAgent)) {
    browser.name = 'Firefox';
    browser.isFirefox = true;
    
    const firefoxVersion = userAgent.match(/Firefox\/([0-9.]+)/);
    if (firefoxVersion) {
      browser.version = firefoxVersion[1];
    }
  }

  // Detect mobile
  browser.isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);

  // Test API support
  browser.supportsMediaRecorder = typeof MediaRecorder !== 'undefined';
  
  // Test capture stream support (more comprehensive check)
  const video = document.createElement('video');
  browser.supportsCaptureStream = 
    typeof video.captureStream === 'function' || 
    typeof video.mozCaptureStream === 'function' ||
    (browser.isChrome && parseFloat(browser.version) >= 51) ||
    (browser.isFirefox && parseFloat(browser.version) >= 43) ||
    (browser.isEdge && parseFloat(browser.version) >= 79);
  
  // Test native HLS support
  if (!browser.hasNativeHlsSupport) {
    browser.hasNativeHlsSupport = video.canPlayType('application/vnd.apple.mpegurl') !== '';
  }

  return browser;
};

/**
 * Check if the current browser is Safari
 * @returns {boolean} True if Safari
 */
export const isSafari = () => {
  return getBrowserInfo().isSafari;
};

/**
 * Check if the current browser is Chrome
 * @returns {boolean} True if Chrome
 */
export const isChrome = () => {
  return getBrowserInfo().isChrome;
};

/**
 * Check if the current browser is Firefox
 * @returns {boolean} True if Firefox
 */
export const isFirefox = () => {
  return getBrowserInfo().isFirefox;
};

/**
 * Check if the current browser is Edge
 * @returns {boolean} True if Edge
 */
export const isEdge = () => {
  return getBrowserInfo().isEdge;
};

/**
 * Check if the current browser supports video capture stream
 * @returns {boolean} True if captureStream is supported
 */
export const supportsCaptureStream = () => {
  const video = document.createElement('video');
  return typeof video.captureStream === 'function' || typeof video.mozCaptureStream === 'function';
};

/**
 * Check if the current browser has native HLS support
 * @returns {boolean} True if native HLS is supported
 */
export const hasNativeHlsSupport = () => {
  const video = document.createElement('video');
  return video.canPlayType('application/vnd.apple.mpegurl') !== '';
};

/**
 * Check if the current browser supports MediaRecorder API
 * @returns {boolean} True if MediaRecorder is supported
 */
export const supportsMediaRecorder = () => {
  return typeof MediaRecorder !== 'undefined';
};

/**
 * Get the best strategy for HLS video handling based on browser capabilities
 * @returns {object} Strategy object with recommended approach
 */
export const getHlsStrategy = () => {
  const browserInfo = getBrowserInfo();
  
  const strategy = {
    type: 'unsupported',
    canDownload: false,
    canConvert: false,
    shouldUseFallback: false,
    fallbackReason: '',
    recommendations: []
  };

  if (browserInfo.isSafari) {
    // Safari has native HLS support but limited captureStream support
    strategy.type = 'safari-native';
    strategy.canDownload = false; // Safari doesn't support captureStream reliably
    strategy.canConvert = false;
    strategy.shouldUseFallback = true;
    strategy.fallbackReason = 'Safari does not support video.captureStream() API needed for download conversion';
    strategy.recommendations = [
      'Open HLS stream directly in new tab',
      'Use Safari\'s native HLS playback',
      'Consider using external tools for downloading'
    ];
  } 
  else if (browserInfo.isChrome || browserInfo.isEdge) {
    // Chrome and Edge need hls.js but support captureStream
    strategy.type = 'hlsjs-capture';
    strategy.canDownload = browserInfo.supportsCaptureStream && browserInfo.supportsMediaRecorder;
    strategy.canConvert = strategy.canDownload;
    strategy.shouldUseFallback = !strategy.canDownload;
    
    if (!strategy.canDownload) {
      strategy.fallbackReason = 'Browser lacks required APIs for video conversion';
      strategy.recommendations = [
        'Update to latest Chrome/Edge version',
        'Check if MediaRecorder API is enabled',
        'Use fallback to open stream directly'
      ];
    } else {
      strategy.recommendations = [
        'Use hls.js for stream loading',
        'Use captureStream() + MediaRecorder for conversion',
        'Full download conversion support available'
      ];
    }
  }
  else if (browserInfo.isFirefox) {
    // Firefox needs hls.js and has mozCaptureStream
    strategy.type = 'firefox-capture';
    strategy.canDownload = browserInfo.supportsCaptureStream && browserInfo.supportsMediaRecorder;
    strategy.canConvert = strategy.canDownload;
    strategy.shouldUseFallback = !strategy.canDownload;
    
    if (!strategy.canDownload) {
      strategy.fallbackReason = 'Firefox lacks required APIs for video conversion';
      strategy.recommendations = [
        'Update to latest Firefox version',
        'Check if MediaRecorder API is enabled',
        'Use fallback to open stream directly'
      ];
    } else {
      strategy.recommendations = [
        'Use hls.js for stream loading',
        'Use mozCaptureStream() + MediaRecorder for conversion',
        'Full download conversion support available'
      ];
    }
  }
  else {
    // Unknown or unsupported browser
    strategy.type = 'unknown';
    strategy.shouldUseFallback = true;
    strategy.fallbackReason = 'Unknown browser - compatibility uncertain';
    strategy.recommendations = [
      'Try using Chrome, Firefox, Edge, or Safari',
      'Open stream directly as fallback',
      'Consider using external download tools'
    ];
  }

  return strategy;
};

/**
 * Check if browser should use fallback for HLS downloads
 * @returns {boolean} True if fallback should be used
 */
export const shouldUseFallbackForHls = () => {
  const strategy = getHlsStrategy();
  return strategy.shouldUseFallback;
};

/**
 * Get user-friendly browser name for display
 * @returns {string} Display name of the browser
 */
export const getBrowserDisplayName = () => {
  const info = getBrowserInfo();
  return `${info.name} ${info.version}`;
};

/**
 * Log browser capabilities for debugging
 */
export const logBrowserCapabilities = () => {
  const info = getBrowserInfo();
  const strategy = getHlsStrategy();
  
  console.log('🔍 [Browser Detection] Browser Info:', info);
  console.log('🔍 [Browser Detection] HLS Strategy:', strategy);
  
  console.log('🔍 [Browser Detection] Capabilities Summary:');
  console.log(`  - Browser: ${info.name} ${info.version}`);
  console.log(`  - Native HLS: ${info.hasNativeHlsSupport ? '✅' : '❌'}`);
  console.log(`  - CaptureStream: ${info.supportsCaptureStream ? '✅' : '❌'}`);
  console.log(`  - MediaRecorder: ${info.supportsMediaRecorder ? '✅' : '❌'}`);
  console.log(`  - Download Support: ${strategy.canDownload ? '✅' : '❌'}`);
  console.log(`  - Conversion Support: ${strategy.canConvert ? '✅' : '❌'}`);
  console.log(`  - Should Use Fallback: ${strategy.shouldUseFallback ? '✅' : '❌'}`);
  
  if (strategy.fallbackReason) {
    console.log(`  - Fallback Reason: ${strategy.fallbackReason}`);
  }
  
  if (strategy.recommendations.length > 0) {
    console.log('  - Recommendations:');
    strategy.recommendations.forEach(rec => {
      console.log(`    • ${rec}`);
    });
  }
};

// Auto-log capabilities in development
if (process.env.NODE_ENV === 'development') {
  logBrowserCapabilities();
}

const browserDetection = {
  getBrowserInfo,
  isSafari,
  isChrome,
  isFirefox,
  isEdge,
  supportsCaptureStream,
  hasNativeHlsSupport,
  supportsMediaRecorder,
  getHlsStrategy,
  shouldUseFallbackForHls,
  getBrowserDisplayName,
  logBrowserCapabilities
};

export default browserDetection;